/**
 * <UserStatusDot /> (R100.56, 2026-05-30)
 *
 * Kleiner farbiger Punkt oben rechts am Profilbild:
 *   - grün, pulsierend: User ist aktuell online (Heartbeat in den letzten 3 Min)
 *   - blau, statisch:   User war heute schon online, ist aktuell nicht
 *   - nichts:           User war seit gestern oder länger nicht online
 *
 * Datenschutz: Status wird über GET /api/presence ermittelt und ist nur im
 * eingeloggten Bereich abrufbar. Keine exakten Login-Zeitstempel sichtbar.
 */

'use client'

import { useEffect, useState } from 'react'

export type PresenceStatus = 'online' | 'today' | 'idle'

interface UserStatusDotProps {
  userId: string
  size?: number
  className?: string
  title?: string
}

export function UserStatusDot({ userId, size = 10, className = '', title }: UserStatusDotProps) {
  const [status, setStatus] = useState<PresenceStatus>('idle')

  useEffect(() => {
    let aborted = false
    fetch(`/api/presence?userIds=${encodeURIComponent(userId)}`, { credentials: 'same-origin' })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (aborted) return
        const s = j?.data?.[userId]
        if (s === 'online' || s === 'today' || s === 'idle') setStatus(s)
      })
      .catch(() => {})
    return () => {
      aborted = true
    }
  }, [userId])

  if (status === 'idle') return null

  const color = status === 'online' ? '#22c55e' : '#3b82f6'
  const label = status === 'online' ? 'aktuell online' : 'heute schon online gewesen'

  // R102.6 (Stephan-Wunsch 2026-05-31): Tooltip im FGL-Sprechblasen-Design
  // (FloatingTooltip via data-tip) statt nativem Browser-title. Das wird vom
  // globalen <FloatingTooltip />-Mount im Root-Layout aufgegriffen und als
  // gold-akzentuierte Sprechblase mit 350 ms Hover-Delay gerendert.
  return (
    <span
      className={`inline-block rounded-full ${status === 'online' ? 'fgl-presence-pulse' : ''} ${className}`}
      style={{
        width: size,
        height: size,
        background: color,
        boxShadow: status === 'online' ? `0 0 0 2px rgba(34,197,94,0.18)` : `0 0 0 2px rgba(59,130,246,0.16)`,
      }}
      data-tip={title || label}
      aria-label={title || label}
    />
  )
}
